import { v4 as uuidv4 } from "uuid";

import {
  FieldDefinitionType,
  FieldDefinitionTypeType,
  TableColumnType,
  TreeColumnType,
} from "@/components/@types";
import { Values } from "@/constants/values";
import {
  ExpandedObjectFieldDefinitionType,
  ObjectType,
} from "@/objects/@types";
import _ from "lodash";

/**
 * Returns a field union type from an object field type.
 *
 * @param objectFieldDefinition - The object field to convert, which contains type, getValue, getValueOptions and other properties
 * @returns A FieldUnionType containing the field variant, type, getValue function, value options and styling
 */
export const toFieldDefinition = (
  objectFieldDefinition: ExpandedObjectFieldDefinitionType<any>,
) => {
  return {
    fieldVariant: undefined,
    type: objectFieldDefinition.type,
    getValue: objectFieldDefinition.getValue,
    getValueOptions: objectFieldDefinition.getValueOptions,
  } as FieldDefinitionType<unknown>;
};

/**
 * Converts an object field definition to a column configuration.
 *
 * @param objectFieldDefinition - The field definition from an object type, containing:
 *   - type: The field data type
 *   - label: Display label
 *   - objectKey: Key in the data object
 *   - inputKey: Optional key for form inputs
 *   - fieldProps: Column-specific configuration like width, locking, etc
 *   - updateProps: Update-related properties like editability
 *   - ReactIcon: Icon component
 * @returns A ColumnType configuration containing:
 *   - Base field properties (type, getValue, etc)
 *   - Column display properties (label, width, locking)
 *   - Column behavior flags (isEditable, isSearchable, etc)
 *   - Column metadata (key, inputKey)
 */
export const toColumn = (
  objectFieldDefinition: ExpandedObjectFieldDefinitionType<any>,
) => {
  return {
    ...toFieldDefinition(objectFieldDefinition),
    // base column type
    label: objectFieldDefinition.label,
    minWidth: objectFieldDefinition.fieldProps?.minWidth,
    maxWidth: objectFieldDefinition.fieldProps?.maxWidth,
    width: objectFieldDefinition.fieldProps.width,
    isLockedLeft: objectFieldDefinition.fieldProps.isLockedLeft ?? false,
    headerFieldType: objectFieldDefinition.fieldProps.headerFieldType,
    ReactIcon: objectFieldDefinition.ReactIcon,
    // column type
    objectKey: objectFieldDefinition.objectKey,
    inputKey: objectFieldDefinition?.inputKey,
    exportPath: objectFieldDefinition.fieldProps?.exportPath,
    isDisabled: objectFieldDefinition.fieldProps?.isDisabled ?? false,
    isMultiline: objectFieldDefinition.fieldProps?.isMultiline ?? false,
    isSearchable: objectFieldDefinition.fieldProps?.isSearchable ?? false,
    isSortable: objectFieldDefinition.fieldProps?.isSortable ?? false,
    isFilterable: objectFieldDefinition.fieldProps?.isFilterable ?? false,
    isResizable: objectFieldDefinition.fieldProps?.isResizable ?? true,
    getEditableObjectInfo:
      objectFieldDefinition.fieldProps?.getEditableObjectInfo,
  } as TableColumnType;
};

/**
 * Converts an object to an array of columns.
 *
 * @param object - The object to convert, which is one of the values from the `objects` collection.
 * @returns An array of columns, each containing key, label, type, ReactIcon, getValue, and more properties.
 */
export const toColumns: (object: ObjectType<any>) => TableColumnType[] = (
  object,
) => {
  return object.fieldDefinitions
    .filter((objectFieldDefinition) => objectFieldDefinition.fieldProps)
    .map((objectFieldDefinition) => toColumn(objectFieldDefinition));
};

/**
 * Converts an object and a mapping configuration into an array of tree columns.
 *
 * @param object - The object containing fields to be mapped.
 * @param map - An array of mapping configurations, each containing:
 *   - `label`: The label for the tree column.
 *   - `branchType`: The type of the branch field.
 *   - `leafType`: The type of the leaf field.
 * @returns An array of `TreeColumnType` objects representing the tree columns.
 */
export const toTreeColumns: (
  object: ObjectType<any>,
  map: {
    label: string;
    branchType: FieldDefinitionTypeType;
    leafType: FieldDefinitionTypeType;
  }[],
) => TreeColumnType[] = (object, map) => {
  const treeColumns: TreeColumnType[] = [];

  const invertedObjectFields = object.fieldDefinitions.reduce(
    (acc, fieldDefinition) => {
      acc[fieldDefinition.objectKey as string] = fieldDefinition;
      return acc;
    },
    {} as Record<string, ObjectType<any>["fieldDefinitions"][number]>,
  );

  for (const mapItem of map) {
    const branchType = mapItem.branchType;
    const leafType = mapItem.leafType;
    const branchField = invertedObjectFields?.[branchType];
    const leafField = invertedObjectFields?.[leafType];

    if (branchField && leafField) {
      treeColumns.push({
        label: branchField.label,
        width:
          Math.max(
            branchField.fieldProps?.width,
            leafField.fieldProps?.width,
          ) || Values.DEFAULT_COLUMN_WIDTH,
        isLockedLeft: branchField.fieldProps?.isLockedLeft || false,
        isLockedRight: branchField.fieldProps?.isLockedRight || false,
        headerFieldType: branchField?.type,
        branchFieldType: branchField.type,
        leafFieldType: leafField.type,
        getBranchValue: branchField.getValue,
        getLeafValue: leafField.getValue,
      } as TreeColumnType);
    }
  }
  return treeColumns;
};

/**
 * Converts a base64 encoded ID to its corresponding identifier string.
 *
 * @param id - The base64 encoded ID string to decode
 * @returns The identifier portion of the decoded ID (the part after the colon)
 * @example
 * // Returns "abc123"
 * IDToIdentifier("T2JqZWN0OmFiYzEyMw==")
 */
export const idToIdentifier = (id: string) => {
  if (!id || id == null) {
    return null;
  }
  // Relay global IDs are base64. Local/temp ids (e.g. `node-1776800851662`)
  // are not — return null so callers can fall back to the raw id.
  let decoded: string;
  try {
    decoded = atob(id);
  } catch {
    return null;
  }
  const split = decoded.split(":");
  return split[1];
};

/**
 * Converts an identifier and typename to a base64 encoded ID.
 *
 * @param identifier - The identifier string to encode
 * @param typename - The typename of the object
 * @returns The base64 encoded ID
 */
export const identifierAndTypenameToId = (
  identifier: string,
  typename: string,
) => {
  // b64 encode
  const encoded = btoa(`${typename}:${identifier}`);
  // return the encoded string
  return encoded;
};

/**
 * Converts a typename to a human readable name.
 *
 * @param typename - The typename to convert
 * @returns The human readable name
 */
export const typenameToHumanName = (typename: string) => {
  // Remove "Object", split on uppercase boundaries, join with spaces, then capitalize
  return _.capitalize(
    typename
      .replace("Object", "")
      .split(/(?=[A-Z])/)
      .join(" ")
      .trim(),
  );
};

/**
 * Generates a fake identifier for a temporary object.
 *
 * @returns A fake identifier
 */
export const fakeIdentifier = () => {
  return `temporary-${Date.now()}`;
};

/**
 * Converts a base64 encoded ID to its corresponding typename.
 *
 * @param id - The base64 encoded ID string to decode
 * @returns The typename portion of the decoded ID (the part before the colon)
 * @example
 * // Returns "T2JqZWN0"
 * idToTypename("T2JqZWN0OmFiYzEyMw==")
 */
export const idToTypename = (id: string) => {
  try {
    // b64 decode
    const decoded = atob(id);
    // split on :
    const split = decoded.split(":");
    // return the first element
    return split[0];
  } catch {
    // Return null if there's an error (invalid base64, etc.)
    return null;
  }
};

/**
 * True if the given Relay GlobalID is a frontend-only sentinel used for
 * client-side panel state (e.g. ``WORKFLOW_EXECUTION_PANEL_ID``).
 * These ids are never resolvable on the
 * backend and must be skipped before reaching ``node(id)``. Detection is
 * by id-shape: the identifier portion is the literal string ``"panel"``
 * rather than a UUID.
 */
export const isSyntheticPanelId = (id: string | null | undefined): boolean => {
  if (!id) return false;
  const identifier = idToIdentifier(id);
  return identifier === "panel";
};

/**
 * Generates missing key values needed for optimistic responses in GraphQL mutations.
 * Creates a new UUID identifier and corresponding base64 encoded ID, along with timestamps.
 *
 * @param typename - The GraphQL typename of the object being created
 * @returns Object containing generated id, identifier, createdAt and updatedAt values
 */
export const generateMissingKeyValuesForOptimisticResponse = (
  typename: string,
): {
  id: string;
  identifier: string;
  name: string;
  createdAt: string;
  updatedAt: string;
} => {
  const identifier = uuidv4();
  const id = identifierAndTypenameToId(identifier, typename);
  const timestamp = new Date().toISOString();

  return {
    id,
    identifier,
    name: "",
    createdAt: timestamp,
    updatedAt: timestamp,
  };
};
